import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { performanceAPI } from '../services/api';

function PerformanceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    costPerRequest: 0,
    tags: [],
    notes: ''
  });

  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchPerformance();
  }, [id]);

  const fetchPerformance = async () => {
    try {
      setLoading(true);
      const response = await performanceAPI.getById(id);
      setPerformance(response.data);
      setFormData({
        costPerRequest: response.data.costPerRequest || 0,
        tags: response.data.tags || [],
        notes: response.data.notes || ''
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await performanceAPI.update(id, formData);
      navigate('/performance');
    } catch (err) {
      alert('Failed to update performance data: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'costPerRequest' ? parseFloat(value) : value
    }));
  };

  const addTag = () => {
    if (tagInput && !formData.tags.includes(tagInput)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !performance) {
    return (
      <div className="card">
        <div className="alert alert-danger">{error}</div>
        <Link to="/performance" className="btn btn-secondary">
          Back to Performance Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <div>
            <h2>{performance.testCaseName}</h2>
            <p className="text-muted">
              <strong>Project:</strong> {performance.project} | <strong>Endpoint:</strong> {performance.endpoint}
            </p>
          </div>
          <Link to="/performance" className="btn btn-secondary">
            Back to Dashboard
          </Link>
        </div>

        {/* Metrics Overview (Read-only) */}
        <h3>Performance Metrics</h3>
        <div className="grid grid-3 mb-4">
          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>Success Rate</h4>
            <h2 style={{ margin: 0 }}>{performance.successRate.toFixed(2)}%</h2>
            <p className="text-muted" style={{ margin: '0.5rem 0 0 0', fontSize: '0.9em' }}>
              {performance.successfulRequests}/{performance.totalRequests} successful
            </p>
          </div>

          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>Avg Latency</h4>
            <h2 style={{ margin: 0 }}>{performance.avgLatency.toFixed(2)}ms</h2>
            <p className="text-muted" style={{ margin: '0.5rem 0 0 0', fontSize: '0.9em' }}>
              P95: {performance.p95Latency.toFixed(0)}ms, P99: {performance.p99Latency.toFixed(0)}ms
            </p>
          </div>

          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>Scale Capacity</h4>
            <h2 style={{ margin: 0 }}>{performance.requestsPerMinute.toLocaleString()}</h2>
            <p className="text-muted" style={{ margin: '0.5rem 0 0 0', fontSize: '0.9em' }}>
              requests per minute
            </p>
          </div>
        </div>

        <div className="grid grid-3 mb-4">
          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>Total Traffic</h4>
            <h2 style={{ margin: 0 }}>{formatBytes(performance.totalTrafficVolume)}</h2>
          </div>

          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>Avg Request Size</h4>
            <h2 style={{ margin: 0 }}>{formatBytes(performance.avgRequestSize)}</h2>
          </div>

          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>Avg Response Size</h4>
            <h2 style={{ margin: 0 }}>{formatBytes(performance.avgResponseSize)}</h2>
          </div>
        </div>

        {/* Editable Fields */}
        <h3 className="mt-4">Edit Details</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Cost Per Request ($)</label>
            <input
              type="number"
              name="costPerRequest"
              value={formData.costPerRequest}
              onChange={handleChange}
              step="0.0001"
              min="0"
              placeholder="0.0000"
            />
            <small className="text-muted">Cost in USD for each request</small>
          </div>

          <div className="form-group">
            <label>Tags</label>
            <div className="flex-between">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add tag"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <button type="button" onClick={addTag} className="btn btn-sm btn-secondary">
                Add Tag
              </button>
            </div>
            <div className="mt-2">
              {formData.tags.map(tag => (
                <span key={tag} className="badge badge-secondary mr-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    style={{ marginLeft: '5px', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Add notes about this endpoint's performance..."
            />
          </div>

          <div className="flex-between mt-4">
            <button
              type="button"
              onClick={() => navigate('/performance')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        <div className="mt-4 pt-3" style={{ borderTop: '1px solid #ddd' }}>
          <p className="text-muted" style={{ fontSize: '0.9em' }}>
            <strong>Last Updated:</strong> {new Date(performance.lastUpdated).toLocaleString()}
          </p>
          {performance.lastTestResultId && (
            <p className="text-muted" style={{ fontSize: '0.9em' }}>
              <strong>Based on Test Result:</strong>{' '}
              <Link to={`/result/${performance.lastTestResultId._id || performance.lastTestResultId}`}>
                View Result
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default PerformanceDetail;
